import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, CardHeader, Input, Modal, ModalFooter, Pagination, StatsCard, StatsGrid } from '@/components/common';
import { BatchPreview, HospitalListUpload } from '@/components/directory/HospitalListUpload';
import { BatchHistory } from '@/pages/insurance/HospitalNetworkPage';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { getErrorMessage } from '@/services';
import { insurersService } from '@/services/insurers';
import {
  hospitalNetworkApi,
  type HospitalSummary,
  type ImportBatchDetail,
  type MatchRow,
  type NetworkHospitalRow,
  type ResolveAction,
} from '@/services/hospitalNetwork';

/**
 * DQ-346: the country admin's view of the cashless hospital network.
 *
 * Overview (counts by scope), Hospitals (scope / insurer filters, merge and
 * undo), Review queue (doubtful matches from insurer lists: link, create new,
 * skip, request correction) and Batches (every insurer's uploads, and upload on
 * behalf of an insurer).
 */

type Tab = 'overview' | 'hospitals' | 'review' | 'batches';
const PAGE_SIZE = 20;

const SCOPE_LABEL: Record<string, string> = {
  all: 'All insurers',
  unique: 'Unique',
  partial: 'Some insurers',
  none: 'No insurer',
};

const REASON_LABEL: Record<string, string> = {
  registration_name_mismatch: 'Same registration number, very different name',
  registration_differs: 'Same name and pincode, different registration number',
  similar_name_same_pincode: 'Similar name in the same pincode',
  same_phone: 'Same phone number, similar name',
  nearby_similar_name: 'Within 300 m, similar name',
};

function useInsurerOptions() {
  // Every insurer the country admin can see, so a list can be uploaded on
  // behalf of an insurer that has none yet.
  const { data = [] } = useQuery({
    queryKey: ['insurer-lookup'],
    queryFn: () => insurersService.lookup(),
    staleTime: 5 * 60 * 1000,
  });
  return data.map((item) => ({ value: item.id, label: item.name }));
}

function Overview() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['hospital-network-summary'],
    queryFn: () => hospitalNetworkApi.networkSummary(),
  });
  if (error) return <p role="alert" className="text-sm text-red-600">{getErrorMessage(error)}</p>;
  if (isLoading || !data) return <p className="text-sm text-gray-500">Loading…</p>;
  return (
    <div className="space-y-6">
      <StatsGrid>
        <StatsCard title="All insurers" value={data.scope_counts.all} />
        <StatsCard title="Some insurers" value={data.scope_counts.partial} />
        <StatsCard title="Unique to one insurer" value={data.scope_counts.unique} />
        <StatsCard
          title="Awaiting review"
          value={data.pending_review}
          subtitle={data.pending_over_30_days ? `${data.pending_over_30_days} older than 30 days` : undefined}
        />
      </StatsGrid>
      <Card>
        <CardHeader title="Hospitals per insurer" />
        {data.per_insurer.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No insurer has uploaded a hospital list yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {data.per_insurer.map((row) => (
              <li key={row.id} className="py-2 flex justify-between text-sm">
                <span className="text-gray-900 dark:text-white">{row.name}</span>
                <span className="text-gray-600 dark:text-gray-300">{row.mapped}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function MergeModal({ source, onClose }: { source: NetworkHospitalRow; onClose: () => void }) {
  const search = useDebouncedSearch(source.name.split(' ')[0] ?? '', 300);
  const [target, setTarget] = useState<NetworkHospitalRow | null>(null);
  const [note, setNote] = useState('');
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['hospital-network-merge-search', search.debouncedTerm],
    queryFn: () => hospitalNetworkApi.networkHospitals({ q: search.debouncedTerm }),
  });
  const merge = useMutation({
    mutationFn: () => hospitalNetworkApi.merge(source.provider_id, (target as NetworkHospitalRow).provider_id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-network'] });
      queryClient.invalidateQueries({ queryKey: ['hospital-network-summary'] });
      toast.success(`${source.name} merged into ${target?.name}. Undo it from "Merged hospitals".`);
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const options = (data?.results ?? []).filter((row) => row.provider_id !== source.provider_id);
  return (
    <Modal isOpen onClose={onClose} title={`Merge ${source.name}`} size="lg">
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
        {source.name} ({source.city_name} {source.pincode}) is hidden and its insurer mappings move to the hospital you
        choose. Where both have the same insurer, the surviving hospital&apos;s mapping is kept.
      </p>
      <Input name="merge_search" label="Merge into" value={search.term} onChange={(e) => search.setTerm(e.target.value)} />
      <ul className="mt-2 max-h-60 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
        {options.map((row) => (
          <li key={row.provider_id}>
            <label className="flex items-center gap-2 py-2 text-sm">
              <input
                type="radio"
                name="merge_target"
                checked={target?.provider_id === row.provider_id}
                onChange={() => setTarget(row)}
              />
              <span className="text-gray-900 dark:text-white">{row.name}</span>
              <span className="text-gray-500">
                {row.city_name} {row.pincode} · {row.insurer_count} insurer(s)
              </span>
            </label>
          </li>
        ))}
      </ul>
      <Input name="merge_note" label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => merge.mutate()} disabled={!target} isLoading={merge.isPending}>
          Merge
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function Hospitals({
  insurerOptions,
  initialInsurer = '',
}: {
  insurerOptions: { value: string; label: string }[];
  initialInsurer?: string;
}) {
  const search = useDebouncedSearch('', 300);
  const [scope, setScope] = useState('');
  const [insurer, setInsurer] = useState(initialInsurer);
  const [merged, setMerged] = useState(false);
  const [page, setPage] = useState(1);
  const [merging, setMerging] = useState<NetworkHospitalRow | null>(null);
  const queryClient = useQueryClient();
  const params = { q: search.debouncedTerm, scope, insurer, page, merged: merged ? 'true' : '' };
  const { data, isLoading, error } = useQuery({
    queryKey: ['hospital-network', params],
    queryFn: () => hospitalNetworkApi.networkHospitals(params),
  });
  const unmerge = useMutation({
    mutationFn: (row: NetworkHospitalRow) => hospitalNetworkApi.unmerge(row.provider_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-network'] });
      toast.success('Merge undone.');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const rows = data?.results ?? [];
  return (
    <Card>
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <Input
          name="network_search"
          placeholder="Name, city, pincode or registration number"
          value={search.term}
          onChange={(e) => {
            search.setTerm(e.target.value);
            setPage(1);
          }}
        />
        <select
          aria-label="Scope"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">Any scope</option>
          <option value="all">All insurers</option>
          <option value="partial">Some insurers</option>
          <option value="unique">Unique</option>
          <option value="none">No insurer</option>
        </select>
        <select
          aria-label="Insurer"
          value={insurer}
          onChange={(e) => setInsurer(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">Any insurer</option>
          {insurerOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={merged} onChange={(e) => setMerged(e.target.checked)} />
          Merged hospitals
        </label>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-red-600">{getErrorMessage(error)}</p>
      ) : isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No hospitals match.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-3 font-medium">Hospital</th>
                <th className="py-2 pr-3 font-medium">City / Pincode</th>
                <th className="py-2 pr-3 font-medium">Scope</th>
                <th className="py-2 pr-3 font-medium">Insurers</th>
                <th className="py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((row) => (
                <tr key={row.provider_id} className="align-top">
                  <td className="py-2 pr-3 text-gray-900 dark:text-white">
                    {row.name}
                    {row.registration_number && <p className="text-xs text-gray-500">Reg. {row.registration_number}</p>}
                    {row.merged_into_name && <p className="text-xs text-gray-500">Merged into {row.merged_into_name}</p>}
                  </td>
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                    {row.city_name} {row.pincode}
                  </td>
                  <td className="py-2 pr-3">
                    <Badge size="sm">{SCOPE_LABEL[row.network_scope] ?? row.network_scope}</Badge>
                  </td>
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                    {row.insurers.map((i) => i.name).join(', ') || '—'}
                  </td>
                  <td className="py-2 text-right">
                    {merged ? (
                      <Button size="sm" variant="ghost" onClick={() => unmerge.mutate(row)} isLoading={unmerge.isPending}>
                        Undo merge
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => setMerging(row)}>
                        Merge…
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(data?.count ?? 0) > PAGE_SIZE && (
        <Pagination
          currentPage={page}
          totalPages={Math.ceil((data?.count ?? 0) / PAGE_SIZE)}
          totalItems={data?.count ?? 0}
          itemsPerPage={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}
      {merging && <MergeModal source={merging} onClose={() => setMerging(null)} />}
    </Card>
  );
}

function Candidate({
  hospital,
  onLink,
  busy,
}: {
  hospital: HospitalSummary;
  onLink: () => void;
  busy: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <p className="text-sm font-medium text-gray-900 dark:text-white">{hospital.name}</p>
      <p className="text-xs text-gray-600 dark:text-gray-300">
        {hospital.address}, {hospital.city_name} {hospital.pincode}
      </p>
      <p className="text-xs text-gray-500">
        Phone {hospital.phone || '—'} · Reg. {hospital.registration_number || '—'}
      </p>
      <p className="text-xs text-gray-500">Insurers: {(hospital.insurers ?? []).join(', ') || 'none'}</p>
      <p className="text-xs text-gray-500">
        Score {hospital.score ?? '—'} · {REASON_LABEL[hospital.reason ?? ''] ?? hospital.reason}
      </p>
      <Button size="sm" className="mt-2" onClick={onLink} isLoading={busy}>
        Link to this hospital
      </Button>
    </div>
  );
}

export function ReviewQueue() {
  const [index, setIndex] = useState(0);
  const [note, setNote] = useState('');
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['hospital-matches'],
    queryFn: () => hospitalNetworkApi.listMatches({ status: 'pending' }),
  });
  const rows = data?.results ?? [];
  const current: MatchRow | undefined = rows[Math.min(index, Math.max(rows.length - 1, 0))];

  const resolve = useMutation({
    mutationFn: (body: { action: ResolveAction; hospital_id?: string; note?: string }) =>
      hospitalNetworkApi.resolveMatch((current as MatchRow).id, body),
    onSuccess: () => {
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['hospital-matches'] });
      queryClient.invalidateQueries({ queryKey: ['hospital-network-summary'] });
      toast.success('Decision recorded.');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (event.key === 'j' || event.key === 'ArrowDown') setIndex((i) => Math.min(i + 1, rows.length - 1));
      if (event.key === 'k' || event.key === 'ArrowUp') setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows.length]);

  if (error) return <p role="alert" className="text-sm text-red-600">{getErrorMessage(error)}</p>;
  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!current) {
    return (
      <Card>
        <p className="text-sm text-gray-500 dark:text-gray-400">Nothing waiting for review.</p>
      </Card>
    );
  }
  const incoming = current.incoming as Record<string, string | null>;
  return (
    <Card>
      <CardHeader
        title={`Row ${current.row_number} from ${current.insurer_name}`}
        subtitle={`${current.batch_name} · ${index + 1} of ${data?.count ?? rows.length} waiting (j / k to move)`}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
          <p className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">Incoming</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{String(incoming.hospital_name ?? '')}</p>
          <p className="text-xs text-gray-700 dark:text-gray-300">
            {String(incoming.address ?? '')}, {String(incoming.city_name ?? '')} {String(incoming.pincode ?? '')}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Phone {incoming.phone || '—'} · Reg. {incoming.registration_number || '—'} · Code{' '}
            {incoming.insurer_hospital_code || '—'}
          </p>
        </div>
        <div className="space-y-2">
          {current.candidates.map((candidate) => (
            <Candidate
              key={candidate.id}
              hospital={candidate}
              busy={resolve.isPending}
              onLink={() => resolve.mutate({ action: 'link', hospital_id: candidate.id, note })}
            />
          ))}
        </div>
      </div>
      <div className="mt-4">
        <Input
          name="resolve_note"
          label="Note (required for a correction request)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => resolve.mutate({ action: 'create_new', note })} disabled={resolve.isPending}>
          Create new hospital
        </Button>
        <Button variant="outline" onClick={() => resolve.mutate({ action: 'skip', note })} disabled={resolve.isPending}>
          Skip row
        </Button>
        <Button
          variant="outline"
          onClick={() => resolve.mutate({ action: 'request_correction', note })}
          disabled={resolve.isPending || !note.trim()}
        >
          Request correction
        </Button>
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={() => setIndex((i) => Math.max(i - 1, 0))} disabled={index === 0}>
            Previous
          </Button>
          <Button
            variant="ghost"
            onClick={() => setIndex((i) => Math.min(i + 1, rows.length - 1))}
            disabled={index >= rows.length - 1}
          >
            Next
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Batches({ insurerOptions }: { insurerOptions: { value: string; label: string }[] }) {
  const [batch, setBatch] = useState<ImportBatchDetail | null>(null);
  const open = async (id: string) => {
    try {
      setBatch(await hospitalNetworkApi.getBatch(id));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  return (
    <div className="space-y-6">
      <HospitalListUpload insurerOptions={insurerOptions} onPreview={setBatch} />
      {batch && <BatchPreview batch={batch} canWrite onChanged={setBatch} />}
      <BatchHistory onOpen={open} />
    </div>
  );
}

const TABS: [Tab, string][] = [
  ['overview', 'Overview'],
  ['hospitals', 'Hospitals'],
  ['review', 'Review queue'],
  ['batches', 'Batches'],
];

export function HospitalNetworkAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = (searchParams.get('tab') as Tab) || 'overview';
  const [tab, setTab] = useState<Tab>(TABS.some(([key]) => key === initial) ? initial : 'overview');
  const insurerOptions = useInsurerOptions();
  return (
    <div>
      <PageHeader
        title="Hospital Network"
        subtitle="Cashless hospitals across insurers: scope, insurer lists and doubtful matches"
        breadcrumbs={[{ label: 'Insurance Companies', href: '/country/insurance-companies' }, { label: 'Hospital Network' }]}
      />
      <div className="mb-4 flex flex-wrap gap-2" role="tablist">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => {
              setTab(key);
              setSearchParams({ tab: key }, { replace: true });
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              tab === key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'overview' && <Overview />}
      {tab === 'hospitals' && (
        <Hospitals insurerOptions={insurerOptions} initialInsurer={searchParams.get('insurer') ?? ''} />
      )}
      {tab === 'review' && <ReviewQueue />}
      {tab === 'batches' && <Batches insurerOptions={insurerOptions} />}
    </div>
  );
}
